/**
 * events.js — San Tomas Tourism College
 * ---------------------------------------------------------------
 * The admissions funnel, expressed once and sent to both Meta and GA4.
 *
 * Page components import from here rather than calling fbq or ReactGA
 * directly. That keeps the marketing taxonomy in one file, means the
 * two platforms can never drift apart, and means adding the Conversions
 * API later is a change to this file only.
 *
 *   Visited any page ................ PageView              (automatic)
 *   Read a course page .............. ViewContent
 *   Searched the course catalogue ... Search
 *   Sent an express-interest form ... Lead                  ← optimise ads on this
 *   Started the application form .... InitiateCheckout
 *   Submitted a full application .... CompleteRegistration  ← the real conversion
 *   Tapped a phone / email link ..... Contact
 *
 * RULE: no name, phone, email, national ID, date of birth, school or
 * uploaded document may ever appear in a parameter below. Meta's
 * Business Tools Terms prohibit it and the Data Protection Act makes it
 * the college's liability, not the agency's. See docs/DATA-PROTECTION.md.
 */

import { trackEvent } from './metaPixel';
import { gaEvent } from './googleAnalytics';

const CURRENCY = 'KES';

/** Modelled worth of one enquiry, for ad bidding. Tune in .env. */
const LEAD_VALUE = Number(process.env.REACT_APP_LEAD_VALUE || 500);

/** Non-refundable admission fee — the real money attached to an application. */
const APPLICATION_VALUE = Number(process.env.REACT_APP_APPLICATION_VALUE || 2500);

/**
 * Meta uses its own event names; GA4 has a parallel recommended set.
 * Send both from one call so reports can be reconciled.
 */
const send = (metaName, metaParams, gaName, gaParams) => {
  trackEvent(metaName, metaParams);
  gaEvent(gaName, gaParams);
};

/** Same department buckets the Programs page filters on. */
export const departmentForId = (id) => {
  const n = Number(id);
  if (n <= 5) return 'Travel and Tourism';
  if (n <= 14) return 'Hospitality, Hotel and Catering';
  if (n <= 30) return 'Business';
  if (n <= 41) return 'Languages';
  if (n <= 47) return 'International College/University Entry Exams';
  return 'Computer';
};

/** A prospective student opened a course page. */
export const trackProgramView = (program) => {
  if (!program) return;
  const fee = program.feePerLevel || program.feePerTerm || null;
  const dept = departmentForId(program.id);

  send(
    'ViewContent',
    {
      content_type: 'product',
      content_ids: [String(program.id)],
      content_name: program.name,
      content_category: dept,
      ...(fee ? { value: Number(fee), currency: CURRENCY } : {}),
    },
    'view_item',
    {
      currency: CURRENCY,
      value: fee ? Number(fee) : 0,
      items: [{ item_id: String(program.id), item_name: program.name, item_category: dept }],
    }
  );
};

/** Someone searched the catalogue. The query is a course name, not PII. */
export const trackProgramSearch = (query, resultCount) => {
  const term = String(query || '').trim().slice(0, 100);
  if (term.length < 3) return;

  send(
    'Search',
    {
      search_string: term,
      content_category: 'Programs',
      ...(typeof resultCount === 'number' ? { num_items: resultCount } : {}),
    },
    'search',
    { search_term: term, ...(typeof resultCount === 'number' ? { results: resultCount } : {}) }
  );
};

/**
 * Express-interest form submitted successfully.
 * This is the event Meta ad sets should optimise for while volume is low.
 */
export const trackInterestLead = (source = 'homepage_express_interest') => {
  send(
    'Lead',
    {
      content_name: 'Express Interest',
      content_category: 'Admissions',
      lead_source: source,
      value: LEAD_VALUE,
      currency: CURRENCY,
    },
    'generate_lead',
    { currency: CURRENCY, value: LEAD_VALUE, lead_source: source }
  );
};

/**
 * Visitor began filling the application form.
 * The form takes 10–15 minutes, so start-vs-finish is the single most
 * useful diagnostic the college has for admissions drop-off.
 */
export const trackApplicationStart = () => {
  send(
    'InitiateCheckout',
    {
      content_name: 'Enrollment Application',
      content_category: 'Admissions',
      value: APPLICATION_VALUE,
      currency: CURRENCY,
    },
    'begin_checkout',
    { currency: CURRENCY, value: APPLICATION_VALUE, item_list_name: 'Enrollment Application' }
  );
};

/**
 * Full application submitted and accepted by the API.
 * Once this reaches roughly 50 events a week, move ad optimisation here.
 *
 * @param {string} preferredProgram — course name only. Never the applicant.
 */
export const trackApplicationComplete = (preferredProgram) => {
  send(
    'CompleteRegistration',
    {
      content_name: preferredProgram || 'Enrollment Application',
      content_category: 'Admissions',
      status: true,
      value: APPLICATION_VALUE,
      currency: CURRENCY,
    },
    'sign_up',
    { currency: CURRENCY, value: APPLICATION_VALUE, method: preferredProgram || 'Application' }
  );
};

/** Tapped a phone number or email address. On mobile this is a real lead. */
export const trackContactClick = (method) => {
  send(
    'Contact',
    {
      content_name: method === 'phone' ? 'Phone tap' : 'Email tap',
      content_category: 'Contact',
    },
    'contact',
    { method: method === 'phone' ? 'phone' : 'email' }
  );
};
